import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardTile } from '../components/CardTile.tsx';
import { SORT_OPTIONS, type SortKey, SubBar, TopBar } from '../components/Chrome.tsx';
import { fx, listCards } from '../lib/api.ts';
import { FX_FALLBACK } from '../lib/format.ts';
import type { CardSummary, SignalLabel } from '../lib/types.ts';

type FilterKind = 'ALL' | SignalLabel | 'BURST' | 'ARB';

/**
 * Comparator factory for the dashboard sort dropdown. Nulls always sink to the
 * end regardless of direction, so a freshly-added card without a price doesn't
 * dominate the top of "price ascending" view.
 */
function sortComparator(sort: SortKey): (a: CardSummary, b: CardSummary) => number {
  function nullsLast<T>(av: T | null, bv: T | null, cmp: (a: T, b: T) => number): number {
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return cmp(av, bv);
  }
  switch (sort) {
    case 'price-desc':
      return (a, b) => nullsLast(a.price, b.price, (x, y) => y - x);
    case 'price-asc':
      return (a, b) => nullsLast(a.price, b.price, (x, y) => x - y);
    case 'pop-desc':
      return (a, b) => nullsLast(a.pop, b.pop, (x, y) => y - x);
    case 'pop-asc':
      return (a, b) => nullsLast(a.pop, b.pop, (x, y) => x - y);
    case 'score-desc':
      // Score tied? Fall back to BUY → HOLD → SELL.
      return (a, b) => {
        const ds = b.score - a.score;
        if (ds !== 0) return ds;
        const order = { BUY: 0, HOLD: 1, SELL: 2 } as const;
        return order[a.signal] - order[b.signal];
      };
    case 'd1-desc':
      return (a, b) => nullsLast(a.d1, b.d1, (x, y) => y - x);
    case 'd1-asc':
      return (a, b) => nullsLast(a.d1, b.d1, (x, y) => x - y);
    case 'd30-desc':
      return (a, b) => nullsLast(a.d30, b.d30, (x, y) => y - x);
    case 'd30-asc':
      return (a, b) => nullsLast(a.d30, b.d30, (x, y) => x - y);
    case 'name-asc':
      return (a, b) => (a.jpEn ?? a.jp).localeCompare(b.jpEn ?? b.jp);
    case 'spread-desc':
      // Actionable spreads first; then by spread % descending. Cards with no
      // arb data sink to the bottom regardless.
      return (a, b) => {
        const ra = a.arb?.actionable ? 1 : 0;
        const rb = b.arb?.actionable ? 1 : 0;
        if (ra !== rb) return rb - ra;
        const pa = a.arb?.netSpreadPct ?? Number.NEGATIVE_INFINITY;
        const pb = b.arb?.netSpreadPct ?? Number.NEGATIVE_INFINITY;
        return pb - pa;
      };
  }
}

export function Dashboard() {
  const navigate = useNavigate();
  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: listCards });
  const fxQ = useQuery({ queryKey: ['fx'], queryFn: fx, refetchInterval: 60_000 });

  // URL hash carries filter state per the handoff spec.
  const initialFilter = (() => {
    const m = window.location.hash.match(/filter=([A-Z]+)/);
    return (m?.[1] as FilterKind | undefined) ?? 'ALL';
  })();
  const [filter, setFilter] = useState<FilterKind>(initialFilter);
  const [query, setQuery] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  // Sort persists in localStorage so navigating into a card and back, or
  // reopening the tab tomorrow, restores whatever order you were using.
  const [sort, setSort] = useState<SortKey>(() => {
    try {
      const saved = localStorage.getItem('pq.sort');
      if (saved && SORT_OPTIONS.some((o) => o.value === saved)) return saved as SortKey;
    } catch {
      /* localStorage unavailable (private mode etc.) — fall through */
    }
    return 'price-desc';
  });
  useEffect(() => {
    try {
      localStorage.setItem('pq.sort', sort);
    } catch {
      /* ignore */
    }
  }, [sort]);

  const cards = cardsQ.data ?? [];
  const counts = useMemo(() => {
    const c = { ALL: cards.length, BUY: 0, SELL: 0, HOLD: 0, BURST: 0, ARB: 0 } as Record<
      FilterKind,
      number
    >;
    for (const card of cards) {
      c[card.signal]++;
      if ((card.burstCount ?? 0) >= 5) c.BURST++;
      if (card.arb?.actionable) c.ARB++;
    }
    return c;
  }, [cards]);

  const visible = useMemo(() => {
    const min = priceMin === '' ? null : Number(priceMin);
    const max = priceMax === '' ? null : Number(priceMax);
    const q = query.trim().toLowerCase();
    const filtered = cards.filter((c) => {
      if (filter === 'BURST') {
        if ((c.burstCount ?? 0) < 5) return false;
      } else if (filter === 'ARB') {
        if (!c.arb?.actionable) return false;
      } else if (filter !== 'ALL' && c.signal !== filter) return false;
      if (min !== null && Number.isFinite(min) && (c.price === null || c.price < min)) return false;
      if (max !== null && Number.isFinite(max) && (c.price === null || c.price > max)) return false;
      if (q.length > 0) {
        const hay = [c.jp, c.jpEn ?? '', c.set, c.num, c.productId, `${c.set} ${c.num}`]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return [...filtered].sort(sortComparator(sort));
  }, [cards, filter, query, priceMin, priceMax, sort]);

  // Persist filter in URL hash so refresh keeps the cut.
  function applyFilter(f: FilterKind) {
    setFilter(f);
    if (f === 'ALL') window.history.replaceState(null, '', '/');
    else window.history.replaceState(null, '', `/#filter=${f}`);
  }

  // Infinite scroll: render the first PAGE_SIZE cards, bump by PAGE_SIZE every
  // time the bottom sentinel scrolls into view. Reset to PAGE_SIZE whenever the
  // filtered set changes (filter/sort/query/price), otherwise jumping from a
  // 50-card SELL view back to a 287-card ALL would land at the limit you had
  // for the previous filter.
  const PAGE_SIZE = 100;
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — we reset on filter changes
  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [filter, sort, query, priceMin, priceMax]);

  const shown = visible.slice(0, visibleLimit);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleLimit((n) => Math.min(n + PAGE_SIZE, visible.length));
        }
      },
      // Trigger before the user actually reaches the bottom so the next batch
      // is ready by the time they get there.
      { rootMargin: '600px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible.length]);

  // 7-column grid when filtered (per the handoff), 8 when 'all'.
  const gridCls = filter === 'ALL' ? 'grid grid-8' : 'grid grid-7';

  return (
    <div className="screen screen-desktop">
      <TopBar
        cardCount={counts.ALL}
        lastSync={cardsQ.isFetching ? 'syncing…' : 'live'}
        fx={fxQ.data?.usd_hkd ?? FX_FALLBACK.usd_hkd}
      />
      <SubBar
        totalCards={counts.ALL}
        count={visible.length}
        sort={sort}
        onSortChange={setSort}
        query={query}
        onQueryChange={setQuery}
        priceMin={priceMin}
        onPriceMinChange={setPriceMin}
        priceMax={priceMax}
        onPriceMaxChange={setPriceMax}
        filters={[
          {
            label: 'All',
            value: counts.ALL,
            active: filter === 'ALL',
            onClick: () => applyFilter('ALL'),
          },
          {
            label: 'HOT',
            value: counts.BUY,
            active: filter === 'BUY',
            tone: 'buy',
            onClick: () => applyFilter('BUY'),
          },
          {
            label: 'SELL',
            value: counts.SELL,
            active: filter === 'SELL',
            tone: 'sell',
            onClick: () => applyFilter('SELL'),
          },
          {
            label: 'HOLD',
            value: counts.HOLD,
            active: filter === 'HOLD',
            onClick: () => applyFilter('HOLD'),
          },
          {
            label: '🔥 Burst',
            value: counts.BURST,
            active: filter === 'BURST',
            onClick: () => applyFilter('BURST'),
          },
          {
            label: '◆ Arb',
            value: counts.ARB,
            active: filter === 'ARB',
            tone: 'arb',
            axisSep: true,
            onClick: () => applyFilter('ARB'),
          },
        ]}
      />
      {cardsQ.isLoading ? (
        <div className="loading" style={{ padding: 40, color: 'var(--ink-dim)' }}>
          loading…
        </div>
      ) : visible.length === 0 ? (
        <EmptyState filter={filter} counts={counts} onResetFilter={() => applyFilter('ALL')} />
      ) : (
        <>
          <div className={gridCls}>
            {shown.map((c) => (
              <CardTile
                key={c.id}
                card={c}
                fx={fxQ.data ?? FX_FALLBACK}
                sort={sort}
                onClick={() => navigate(`/card/${c.id}`)}
              />
            ))}
          </div>
          {shown.length < visible.length && (
            <div
              ref={sentinelRef}
              className="loading"
              style={{ padding: 24, color: 'var(--ink-mute)', textAlign: 'center', fontSize: 12 }}
            >
              loading more… ({shown.length} / {visible.length})
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface EmptyStateProps {
  filter: FilterKind;
  counts: Record<FilterKind, number>;
  onResetFilter: () => void;
}

function EmptyState({ filter, counts, onResetFilter }: EmptyStateProps) {
  // Uses the design's `.empty` / `.empty-card` / `.empty-stats` classes verbatim.
  return (
    <div className="empty" role="status">
      <div className="empty-card">
        <div className="empty-mark">∅</div>
        <h2>{filter === 'ALL' ? 'No signals firing right now' : `No ${filter} signals firing`}</h2>
        <p>
          {filter === 'ALL'
            ? `All ${counts.ALL} watched cards are flat. The composite score requires several sub-signals above threshold; right now the market is quiet across every tracked set.`
            : 'Sub-signals are tracked but nothing’s crossed the buy/sell threshold for this filter.'}
        </p>
        <div className="empty-stats">
          <div className="stat">
            <div className="v mono">{counts.ALL}</div>
            <div className="k">watched</div>
          </div>
          <div className="stat">
            <div className="v mono">{counts.BUY}</div>
            <div className="k">HOT</div>
          </div>
          <div className="stat">
            <div className="v mono">{counts.SELL}</div>
            <div className="k">SELL</div>
          </div>
          <div className="stat">
            <div className="v mono">{counts.HOLD}</div>
            <div className="k">HOLD</div>
          </div>
        </div>
        {filter !== 'ALL' && (
          <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
            <button type="button" className="chip" onClick={onResetFilter}>
              Show all {counts.ALL}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
